import { useCallback, useMemo, useState } from "react";
import { bool, string, object, node, arrayOf, number, oneOfType } from "prop-types";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { useTranslation } from "react-i18next";
import { useEditorWebsocket } from "@hooks";
import { useEstimateEditorStore, useEstimateStructure } from "@tools";
import { Alert, SortableList } from "@components";
import { BreakdownRowGroup } from "./BreakdownRowGroup/BreakdownRowGroup";

export const BreakdownRowGroupList = observer(({
  element,
  isLastGroupRow,
  hashedPath,
  children,
  disabled,
  tableDepth = 0,
  initialDepth = 0,
  ...elementProps
}) => {
  const structure = useEstimateStructure();
  const socket = useEditorWebsocket();
  const { currentVersionKey } = useEstimateEditorStore();
  const { t } = useTranslation();

  const [moveAlertVisible, showMoveAlert] = useState(false);
  const [mergeData, setMergeData] = useState(undefined);

  const { displayChildren } = element;

  const actualDepthLevel = useMemo(() => tableDepth + initialDepth, []);
  
  const handleChildrenReorder = useCallback((list, movedElementId) => {
    element.reorderChildren(list, list.length < displayChildren.length, movedElementId);
    structure.historyManager.stopGroup();
  }, [ currentVersionKey, displayChildren, displayChildren.length ])
  
  const handleListReorderStart = () => {
    structure.historyManager.startGroup();
  }
  
  const handleDragAdd = useCallback((e) => {
    if (!e.from) return;

    const movedTask = structure.getByHashedIdPath(
      ...e.from.split("/").slice(1),
      e.item
    );
    
    if (!movedTask) return;

    const target = element.getExistingChild(
      movedTask.id,
      movedTask.componentId,
      movedTask.name
    );
    
    if (target) {
      showMoveAlert(true);
      movedTask.switchParentBlockChildReorder();
      
      setMergeData({
        target,
        movedTask,
      });
    } else {
      element.pushChild(getSnapshot(movedTask), e.newIndex);
      const oldPath = movedTask.treePath.join("/");
      const path = [...element.treePath, ...movedTask.treePath.slice(element.treePath.length)].join("/");
      
      socket?.requestCommentMove(oldPath, path);
      structure.historyManager.addCommentPathPatch(oldPath, path);
    }
  }, [ currentVersionKey ]);

  const hideMoveAlert = () => {
    showMoveAlert(false);
  };

  const mergeChildren = () => {
    hideMoveAlert();
    if (!mergeData) return;
    structure.historyManager.startGroup();
    mergeData.target.mergeWithOtherTask(getSnapshot(mergeData.movedTask), true);
    
    const oldPath = mergeData.movedTask.treePath.join("/");
    const path = mergeData.target.treePath.join("/");
    
    socket?.requestCommentMove(oldPath, path);
    structure.historyManager.addCommentPathPatch(oldPath, path);
    
    mergeData.movedTask.removeSelf();
    setMergeData(undefined);
    structure.historyManager.stopGroup();
  };
  
  return (
    <>
      <SortableList
        list={displayChildren}
        path={hashedPath.join("/")}
        group={`breakdown-d${actualDepthLevel}`}
        onListReorderStart={handleListReorderStart}
        onListReorder={handleChildrenReorder}
        onListAdd={handleDragAdd}
        displayAsColumnFlexbox
        disabled={disabled || structure.hasSearchPhrase}
      >
        {
          displayChildren?.map((child, i) => (
            <BreakdownRowGroup
              key={`element${child.id}`}
              element={child}
              isLastGroupRow={
                isLastGroupRow &&
                (tableDepth === 0 || i === displayChildren.length - 1)
              }
              tableDepth={tableDepth + 1}
              initialDepth={initialDepth}
              {...elementProps}
            />
          ))
        }
      </SortableList>
      {children}
      <Alert
        isOpen={moveAlertVisible}
        title={t(
          "views.editor.dialogs.move_alert.title_level" + (tableDepth + 1)
        )}
        onAccept={mergeChildren}
        onCancel={hideMoveAlert}
        acceptText={t("views.editor.dialogs.move_alert.confirm")}
        cancelText={t("common.cancel")}
      >
        {t("views.editor.dialogs.move_alert.subtitle")}
      </Alert>
    </>
  );
});

BreakdownRowGroupList.propTypes = {
  /** TaskModel | SectionModel */
  element: object.isRequired,
  tableDepth: number,
  initialDepth: number,

  blockExpansions: bool,
  displayLibraryElementStatus: bool,
  allowLibraryElementStatusChange: bool,
  isLastGroupRow: bool,
  
  hashedPath: arrayOf(oneOfType([number, string])),
  parentName: string,
  children: node,
  disabled: bool,
};
